// ═══════════════════════════════════════════════════════════════════════════
// PAGE: FUSION DE DEUX ACTIONS (v0.6.47+ — mai 2026)
// ═══════════════════════════════════════════════════════════════════════════
//
// Route : `#actions/merge/:keeperId/:absorbedId`
//
// UX v2 (cf. retour user 2026-05-20) :
//   • Conservée affichée en NOIR (cellule claire).
//   • Absorbée affichée en BLANC sur fond sombre.
//   • Résultat fusionné en GRIS sur fond clair, ENTIÈREMENT ÉDITABLE
//     (inputs + chips toggle).
//   • Clic sur une valeur (scalaire) côté Conservée/Absorbée → copie dans le
//     résultat.
//   • Clic sur un chip (liste) côté Conservée/Absorbée → toggle ce chip dans
//     le résultat (recheck visuel + ajout/retrait à la liste). Permet de
//     construire l'union ligne par ligne plutôt que tout-ou-rien.
//   • Le résultat peut contenir des valeurs ni dans A, ni dans B (ajout via
//     les sélecteurs intégrés au résultat).
//
// Au submit, on envoie l'état complet du résultat dans `overrides` ; le backend
// applique sur la conservée, bascule toutes les FK et marque l'absorbée
// « Fusionnée » (cf. services/action_merge.py).

import { definePage, navigateTo } from '../router.js';
import {
  _committeeLabel,
  _committeeTitle,
  _getList,
  _initResultFromKeeper,
  _projectLabel,
  _projectTitle,
  _scalarEq,
  _userFull,
  _userLabel,
  _userTitle,
} from './page-action-merge-helpers.js';

export interface MergeUser {
  id: number;
  name: string;
  trigram?: string | null;
  is_team?: boolean;
}

interface MergeStatus {
  id: number;
  name: string;
  position?: number;
  color?: string;
  is_final?: boolean;
}

export interface MergeProject {
  id: number;
  code?: string | null;
  name: string;
}

export interface MergeCommittee {
  id: number;
  code: string;
  name: string;
}

export interface MergeAction extends Record<string, unknown> {
  id: number;
  text?: string | null;
  deadline?: string | null;
  deadline_revised?: string | null;
  status_id?: number | null;
  status_name?: string | null;
  progress?: number;
  priority?: number | null;
  approver_id?: number | null;
  performers?: Array<number | { id: number }>;
  consulted?: Array<number | { id: number }>;
  informed?: Array<number | { id: number }>;
  project_ids?: number[];
  committee_ids?: number[];
  merged_into_id?: number | null;
}

type ScalarField =
  'text' | 'deadline' | 'deadline_revised' | 'priority' | 'status_id' | 'progress' | 'approver_id';
export type ListField = 'performers' | 'consulted' | 'informed' | 'project_ids' | 'committee_ids';

// Ordre d'affichage des lignes du tableau de comparaison (cf. user 2026-05-21).
// L'ordre R-A-C-I est intercalé entre Avancement et Projets. Origine
// décommissionnée le 24/05/2026 (modèle + table + UI supprimés).
type RowDef = { field: ScalarField; list: false } | { field: ListField; list: true };
const ROW_ORDER: RowDef[] = [
  { field: 'text', list: false },
  { field: 'deadline', list: false },
  { field: 'deadline_revised', list: false },
  { field: 'priority', list: false },
  { field: 'status_id', list: false },
  { field: 'progress', list: false },
  { field: 'performers', list: true }, // R
  { field: 'approver_id', list: false }, // A
  { field: 'consulted', list: true }, // C
  { field: 'informed', list: true }, // I
  { field: 'project_ids', list: true },
  { field: 'committee_ids', list: true },
];

export interface ResultState {
  text: string | null;
  deadline: string | null;
  deadline_revised: string | null;
  priority: number | null;
  status_id: number | null;
  progress: number;
  approver_id: number | null;
  performers: number[];
  consulted: number[];
  informed: number[];
  project_ids: number[];
  committee_ids: number[];
}

const PRIORITY_LABELS = ['Très haute', 'Haute', 'Normale', 'Basse', 'Très basse'];

const FIELD_LABEL_KEY: Record<ScalarField | ListField, string> = {
  text: 'pageActionsMerge.field.text',
  deadline: 'pageActionsMerge.field.deadline',
  deadline_revised: 'pageActionsMerge.field.deadlineRevised',
  priority: 'pageActionsMerge.field.priority',
  status_id: 'pageActionsMerge.field.status',
  progress: 'pageActionsMerge.field.progress',
  approver_id: 'pageActionsMerge.field.approver',
  performers: 'pageActionsMerge.field.performers',
  consulted: 'pageActionsMerge.field.consulted',
  informed: 'pageActionsMerge.field.informed',
  project_ids: 'pageActionsMerge.field.projects',
  committee_ids: 'pageActionsMerge.field.committees',
};

// ── Styles inline (palette merge) ──────────────────────────────────────────
// Direction visuelle (cf. user 2026-05-21, ajustement #4) :
//   • Cellules en FOND BLANC strict pour les 3 colonnes.
//   • Pavés Conservée ET Absorbée : NOIR sur GRIS CLAIR (palette unique).
//   • Coches À L'INTÉRIEUR du pavé :
//       – ✓ VERT (var(--accent)) quand sélectionnée,
//       – ✕ ROUGE (var(--danger)) quand non sélectionnée — état explicite
//         plutôt qu'un cercle vide qui restait ambigu.
//   • Pavés Résultat :
//       – VERT clair (var(--accent-bg)) pour les valeurs issues d'une des
//         deux actions sources,
//       – BLEU clair (#dbeafe / #1d4ed8) pour les valeurs « ajoutées »
//         (saisies ou pickées depuis l'extérieur).
//   • Jauge d'avancement : track BLANC bordé, fill NOIR — visible sur fond
//     blanc et neutre.
const MERGE_CSS = `
<style>
  .merge-page { max-width:1180px; margin:0 auto; padding:0 12px; }
  .merge-table { width:100%; border-collapse:collapse; font-size:13px; margin-top:12px;
                 background:#ffffff; }
  .merge-table th, .merge-table td {
    padding:8px 10px; border-bottom:1px solid var(--bd, #e5e7eb); vertical-align:top;
    background:#ffffff;
  }
  .merge-table thead th { background:#ffffff; font-weight:600; text-align:left;
                          border-bottom:2px solid var(--bd, #e5e7eb); }
  .merge-table tr.merge-row > td.field { width:160px; font-weight:600; color:var(--text); }
  .merge-table td.result input,
  .merge-table td.result select,
  .merge-table td.result textarea {
    width:100%; background:#ffffff; color:var(--text);
    border:1px solid var(--bd, #d1d5db); border-radius:4px;
    padding:4px 6px; font-size:13px; font-family:inherit;
  }
  .merge-table td.result textarea { min-height:48px; }
  .merge-table td.result .picker-row { margin-top:6px; display:flex; gap:6px; align-items:center; }
  .merge-table td.result .picker-row select { flex:1; }

  /* Pavé valeur : noir-sur-blanc avec bordure pour rester visible.
     Le différentiateur entre keeper et absorbed est l'en-tête de colonne. */
  .merge-pill {
    display:inline-flex; align-items:center; gap:6px;
    padding:3px 8px; border-radius:6px;
    margin:2px 4px 2px 0; font-size:12px; font-weight:500; line-height:1.4;
    border:1px solid var(--bd, #d1d5db); cursor:pointer; user-select:none;
    max-width:100%; vertical-align:middle;
    background:#ffffff; color:#000;
  }
  .merge-pill:hover { background:#f3f4f6; }
  .merge-pill .merge-pill-value {
    overflow:hidden; text-overflow:ellipsis; white-space:nowrap; max-width:260px;
  }
  .merge-pill.empty .merge-pill-value { color:#9ca3af; font-style:italic; }

  /* Coche dans le pavé :
     ✓ vert si .checked, ✕ rouge sinon. Cercle blanc bordé dans les 2 cas. */
  .merge-check {
    display:inline-block; width:14px; height:14px; position:relative;
    border-radius:50%; background:#ffffff; flex-shrink:0;
    border:1.5px solid var(--danger, #dc2626);
  }
  .merge-pill .merge-check::after {
    content:'✕'; position:absolute;
    top:50%; left:50%; transform:translate(-50%,-50%);
    color:var(--danger, #dc2626); font-size:10px; font-weight:900; line-height:1;
  }
  .merge-pill.checked .merge-check {
    border-color:var(--accent, #1d9e75);
  }
  .merge-pill.checked .merge-check::after {
    content:'✓'; color:var(--accent, #1d9e75);
  }

  /* Pavés du résultat :
       – issus d'une source : style RACI (vert clair),
       – ajoutés (saisis ou pickés hors A/B) : bleu clair.
     Tous deux non-cliquables (cursor:default) avec × pour retirer. */
  .merge-pill.result {
    background:var(--accent-bg, #edfbf4); color:var(--accent, #1d9e75);
    border-color:transparent; cursor:default; padding:2px 7px; border-radius:10px;
  }
  .merge-pill.result:hover { background:var(--accent-bg, #edfbf4); }
  .merge-pill.result.added {
    background:#dbeafe; color:#1d4ed8;
  }
  .merge-pill.result.added:hover { background:#dbeafe; }
  .merge-pill.result .remove {
    cursor:pointer; font-weight:700; color:inherit; margin-left:2px;
    font-size:13px; line-height:1; opacity:.7;
  }
  .merge-pill.result .remove:hover { opacity:1; color:var(--danger, #dc2626); }
  .pill-empty { color:var(--text3, #94a3b8); font-style:italic; font-size:11px; }

  /* Jauge d'avancement : track BLANC bordé, fill NOIR.
     ⚠ display:block sur .merge-gauge-fill OBLIGATOIRE — les <span> par défaut
     sont inline et ignorent height:100%, donc le fill apparaissait à 0 px
     (bug reproduit en V1.3/V1.4). */
  .merge-gauge {
    display:inline-block; width:64px; height:10px;
    background:#ffffff; border:1px solid #1a2540; border-radius:4px; overflow:hidden;
    vertical-align:middle; margin-left:6px; flex-shrink:0; box-sizing:border-box;
  }
  .merge-gauge-fill { display:block; height:100%; background:#000000; transition:width .12s; }
  .progress-row { display:inline-flex; align-items:center; gap:6px; }
  .progress-row input[type="number"] { width:64px; }

  /* Bouton "+" d'ajout RACI (R/C/I) — style modale d'édition d'action. */
  .merge-add-btn {
    display:inline-flex; align-items:center; justify-content:center;
    width:22px; height:22px; padding:0; margin-left:4px;
    border:1px solid var(--bd, #d1d5db); background:#ffffff;
    border-radius:50%; cursor:pointer; font-size:14px; line-height:1;
    color:var(--text2);
  }
  .merge-add-btn:hover { background:var(--bg, #f4f6f9); color:var(--text); }

  /* Boutons "Tous" / "Aucun" en tête de cellule liste (keeper/absorbed). */
  .merge-bulk-btns {
    margin-bottom:4px; display:flex; gap:8px;
  }
  .merge-bulk-btns button {
    background:none; border:none; cursor:pointer; padding:0;
    color:var(--accent, #1d9e75); text-decoration:underline;
    font-size:11px; font-family:inherit;
  }
  .merge-bulk-btns button:hover { color:var(--text); }

  /* Bouton Approbateur (résultat) : déclencheur du picker single-select. */
  .merge-approver-btn {
    display:inline-flex; align-items:center; justify-content:space-between;
    width:100%; padding:4px 8px;
    background:#ffffff; color:var(--text);
    border:1px solid var(--bd, #d1d5db); border-radius:4px;
    cursor:pointer; font-size:13px; font-family:inherit; text-align:left;
  }
  .merge-approver-btn:hover { background:var(--bg, #f4f6f9); }
  .merge-approver-btn .caret { color:var(--text3, #94a3b8); margin-left:6px; }
  .merge-approver-btn.empty { color:var(--text3, #94a3b8); font-style:italic; }
  .merge-raci-picker-dd {
    background:#ffffff; border:1px solid var(--bd, #d1d5db); border-radius:6px;
    box-shadow:0 4px 12px rgba(0,0,0,.15); z-index:1000;
    min-width:260px; max-height:420px; overflow:hidden;
    display:flex; flex-direction:column;
  }
  .merge-raci-picker-dd .search-row {
    padding:8px; border-bottom:1px solid var(--bd, #d1d5db);
  }
  .merge-raci-picker-dd input.search {
    width:100%; border:1px solid var(--bd, #d1d5db); border-radius:4px;
    padding:6px 10px; outline:none; font-size:12px;
  }
  .merge-raci-picker-dd .pick-list { overflow-y:auto; flex:1; }
  .merge-raci-picker-dd label {
    display:flex; align-items:center; gap:7px;
    padding:6px 10px; cursor:pointer; font-size:12px;
  }
  .merge-raci-picker-dd label:hover { background:var(--bg, #f4f6f9); }
  .merge-raci-picker-dd label.sel { background:var(--accent-bg, #edfbf4); }
  .merge-raci-picker-dd .footer {
    padding:8px 10px; border-top:1px solid var(--bd, #d1d5db);
    display:flex; justify-content:flex-end;
  }

  .merge-help { font-size:12px; color:var(--text2); margin-top:4px; font-style:italic; }
  .merge-warn {
    margin-top:18px; padding:10px 12px; background:#fff4e5;
    border:1px solid #f5a623; border-radius:6px; color:#7a4a00;
  }
  .merge-actions { margin-top:18px; display:flex; gap:12px; justify-content:flex-end; }
  .merge-swap {
    display:inline-flex; align-items:center; gap:12px; flex-wrap:wrap;
    margin:8px 0 0 0;
  }
  .merge-swap .pill {
    padding:4px 10px; border-radius:14px; font-size:13px; font-weight:500;
    background:#f3f4f6; color:#000; border:1px solid var(--bd, #d1d5db);
  }
</style>`;

pages.actionsMerge = definePage<'actions/merge/:keeperId/:absorbedId'>(
  async ({ keeperId, absorbedId }) => {
    setHeader(t('pageActionsMerge.title'), '');
    setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);

    if (keeperId === absorbedId) {
      setContent(
        `<div class="error" style="padding:20px">${esc(t('pageActionsMerge.err.invalidSelection'))}</div>`,
      );
      return;
    }

    let allActions: MergeAction[] = [];
    let users: MergeUser[];
    let statuses: MergeStatus[] = [];
    let projects: MergeProject[] = [];
    let committees: MergeCommittee[] = [];
    try {
      const [a1, a2, a3, a4, a5] = (await Promise.all([
        api.getAllActions(),
        loadUsers(),
        loadActionStatuses(),
        api.getProjects(),
        api.getCommittees(),
      ])) as [MergeAction[], MergeUser[], MergeStatus[], MergeProject[], MergeCommittee[]];
      allActions = a1;
      users = a2;
      statuses = a3;
      projects = a4;
      committees = a5;
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : t('common.error');
      setContent(`<div class="error">${esc(msg)}</div>`);
      return;
    }

    // ── État mutable ────────────────────────────────────────────────────
    let curKeeperId = keeperId;
    let curAbsorbedId = absorbedId;
    const findAction = (id: number): MergeAction | undefined => allActions.find((a) => a.id === id);
    const initialKeeper = findAction(curKeeperId);
    if (!initialKeeper) {
      setContent(
        `<div class="error" style="padding:20px">${esc(t('pageActionsMerge.err.notFound'))}</div>`,
      );
      return;
    }
    let result: ResultState = _initResultFromKeeper(initialKeeper);

    // Maps de résolution.
    const userMap: Record<number, MergeUser> = Object.fromEntries(users.map((u) => [u.id, u]));
    const statusMap: Record<number, MergeStatus> = Object.fromEntries(
      statuses.map((s) => [s.id, s]),
    );
    const projectMap: Record<number, MergeProject> = Object.fromEntries(
      projects.map((p) => [p.id, p]),
    );
    const committeeMap: Record<number, MergeCommittee> = Object.fromEntries(
      committees.map((c) => [c.id, c]),
    );

    // Tri stable des users pour les sélecteurs (équipes en bas).
    // Filtre actifs uniquement (cf. user 2026-05-21) — pas d'ajout d'inactifs
    // dans les RACI ou comme approbateur depuis la page de fusion.
    const sortedUsers = [...users.filter((u) => isUserActive(u as never))].sort((a, b) =>
      a.is_team === b.is_team ? a.name.localeCompare(b.name) : a.is_team ? 1 : -1,
    );

    // ── Helpers de rendu ───────────────────────────────────────────────

    // Texte brut affiché dans la pastille (pas de HTML, le wrapper escape).
    // Pour les valeurs absentes, retourne '' — le wrapper ajoute la classe
    // .empty et affiche '—' grisé.
    function _scalarText(field: ScalarField, a: MergeAction | null): string {
      if (!a) return '';
      switch (field) {
        case 'text':
          return a.text || '';
        case 'deadline':
          return a.deadline ? fmtDate(a.deadline) : '';
        case 'deadline_revised':
          return a.deadline_revised ? fmtDate(a.deadline_revised) : '';
        case 'priority':
          return a.priority == null ? '' : `${a.priority} — ${PRIORITY_LABELS[a.priority] ?? '?'}`;
        case 'status_id': {
          const s = a.status_id != null ? statusMap[a.status_id] : null;
          return s ? s.name : '';
        }
        case 'progress':
          return `${a.progress ?? 0} %`;
        case 'approver_id': {
          const u = a.approver_id != null ? userMap[a.approver_id] : null;
          return u ? _userFull(u) : '';
        }
      }
    }

    // Pavé d'une valeur scalaire (côté keeper ou absorbed) :
    //   • <span.merge-pill.keeper|absorbed> cliquable, contient :
    //       – la coche verte (.merge-check) INTÉRIEURE,
    //       – le texte de la valeur (.merge-pill-value),
    //   • Pour `progress`, on ajoute la jauge À CÔTÉ du pavé (pas dedans) —
    //     même format que la liste des actions.
    //   • La coche s'allume quand la valeur de cette source correspond à la
    //     valeur courante du résultat (équivalent VARSAVE / égalité tolérante
    //     aux null).
    function _scalarWrapperHtml(
      field: ScalarField,
      source: 'keeper' | 'absorbed',
      a: MergeAction,
      checked: boolean,
    ): string {
      const text = _scalarText(field, a);
      const empty = text === '';
      const pill =
        `<span class="merge-pill ${source}${checked ? ' checked' : ''}${empty ? ' empty' : ''}" ` +
        `data-scalar-set="${field}" data-source="${source}">` +
        `<span class="merge-check"></span>` +
        `<span class="merge-pill-value">${empty ? '—' : esc(text)}</span>` +
        `</span>`;
      const gauge = field === 'progress' ? _gaugeOnlyHtml(a.progress) : '';
      return pill + gauge;
    }

    function _gaugeOnlyHtml(pct: number | null | undefined): string {
      const w = Math.max(0, Math.min(100, pct ?? 0));
      return `<span class="merge-gauge"><span class="merge-gauge-fill" style="width:${w}%"></span></span>`;
    }

    // Pavé d'un item de liste côté Conservée/Absorbée. `inResult` = coche
    // allumée (l'id est dans la liste du résultat). Le `side` détermine la
    // palette (clair pour keeper, sombre pour absorbed) — actuellement
    // identique sur les 2 côtés (palette unifiée 2026-05-21).
    function _sideChipHtml(
      field: ListField,
      id: number,
      side: 'keeper' | 'absorbed',
      inResult: boolean,
    ): string {
      const label = _chipLabel(field, id);
      const title = _chipTitle(field, id);
      return (
        `<span class="merge-pill ${side}${inResult ? ' checked' : ''}" ` +
        `data-list-toggle="${field}" data-id="${id}" title="${esc(title)}">` +
        `<span class="merge-check"></span>` +
        `<span class="merge-pill-value">${esc(label)}</span>` +
        `</span>`
      );
    }

    function _chipLabel(field: ListField, id: number): string {
      if (field === 'project_ids') return _projectLabel(projectMap[id]);
      if (field === 'committee_ids') return _committeeLabel(committeeMap[id]);
      return _userLabel(userMap[id]);
    }

    // Tooltip d'un chip : pour les users on affiche le nom complet, pour les
    // projets/comités on affiche "<code> - <nom>". Si vide, le browser
    // n'affiche pas de tooltip (title="" est sans effet).
    function _chipTitle(field: ListField, id: number): string {
      if (field === 'project_ids') return _projectTitle(projectMap[id]);
      if (field === 'committee_ids') return _committeeTitle(committeeMap[id]);
      return _userTitle(userMap[id]);
    }

    function _sideListHtml(field: ListField, a: MergeAction, side: 'keeper' | 'absorbed'): string {
      const ids = _getList(field, a);
      const resultIds = new Set(result[field] as number[]);
      // CTAs « Tous » / « Aucun » uniquement si la source porte au moins
      // une valeur — sinon ces boutons n'ont rien à faire.
      const bulk = ids.length
        ? `<div class="merge-bulk-btns">` +
          `<button type="button" data-list-bulk="all" data-field="${field}" data-source="${side}">` +
          `${esc(t('pageActionsMerge.bulk.all'))}</button>` +
          `<button type="button" data-list-bulk="none" data-field="${field}" data-source="${side}">` +
          `${esc(t('pageActionsMerge.bulk.none'))}</button>` +
          `</div>`
        : '';
      const chipsHtml = ids.length
        ? ids.map((id) => _sideChipHtml(field, id, side, resultIds.has(id))).join('')
        : '<span class="pill-empty">—</span>';
      return bulk + chipsHtml;
    }

    // Chip côté Résultat : pavé vert clair (style RACI de la modale) si
    // l'id vient d'une des 2 actions sources, sinon BLEU clair (ajout
    // utilisateur via picker ou modification libre).
    function _resultListHtml(field: ListField): string {
      const ids = result[field] as number[];
      const keeper = findAction(curKeeperId);
      const absorbed = findAction(curAbsorbedId);
      const fromSources = new Set<number>([
        ...(keeper ? _getList(field, keeper) : []),
        ...(absorbed ? _getList(field, absorbed) : []),
      ]);
      const chips = ids
        .map((id) => {
          const label = _chipLabel(field, id);
          const title = _chipTitle(field, id);
          const addedCls = fromSources.has(id) ? '' : ' added';
          return (
            `<span class="merge-pill result${addedCls}" data-id="${id}" title="${esc(title)}">` +
            `<span class="merge-pill-value">${esc(label)}</span>` +
            `<span class="remove" data-list-remove="${field}" data-id="${id}">×</span>` +
            `</span>`
          );
        })
        .join('');
      const picker = _addControlHtml(field, ids);
      return `<div class="result-chips">${chips || '<span class="pill-empty">—</span>'}</div>${picker}`;
    }

    // Contrôle d'ajout dans la cellule résultat. Pour R/C/I : bouton "+"
    // façon modale d'édition d'action (ouvre un picker anchored).
    // Pour Projets/Comités : <select> simple (au cas où l'utilisateur veut
    // ajouter rapidement sans dropdown anchored).
    function _addControlHtml(field: ListField, currentIds: number[]): string {
      if (field === 'performers' || field === 'consulted' || field === 'informed') {
        return `<div class="picker-row"><button type="button" class="merge-add-btn" data-raci-picker="${field}" title="${esc(t('pageActionsMerge.addPlaceholder'))}">+</button></div>`;
      }
      return _pickerHtml(field, currentIds);
    }

    function _pickerHtml(field: ListField, currentIds: number[]): string {
      const excluded = new Set(currentIds);
      let options = '';
      if (field === 'project_ids') {
        for (const p of projects) {
          if (excluded.has(p.id)) continue;
          options += `<option value="${p.id}">${esc(_projectLabel(p))}</option>`;
        }
      } else if (field === 'committee_ids') {
        for (const c of committees) {
          if (excluded.has(c.id)) continue;
          options += `<option value="${c.id}">${esc(c.code)} — ${esc(c.name)}</option>`;
        }
      } else {
        for (const u of sortedUsers) {
          if (excluded.has(u.id)) continue;
          options += `<option value="${u.id}">${esc(u.is_team ? `[équipe] ${u.name}` : _userFull(u))}</option>`;
        }
      }
      if (!options) return '';
      return (
        `<div class="picker-row">` +
        `<select data-list-add="${field}"><option value="">${esc(t('pageActionsMerge.addPlaceholder'))}</option>${options}</select>` +
        `</div>`
      );
    }

    function _resultScalarHtml(field: ScalarField): string {
      switch (field) {
        case 'text':
          return `<textarea data-scalar="text">${esc(result.text || '')}</textarea>`;
        case 'deadline':
          return `<input type="date" data-scalar="deadline" value="${esc(result.deadline || '')}">`;
        case 'deadline_revised':
          return `<input type="date" data-scalar="deadline_revised" value="${esc(result.deadline_revised || '')}">`;
        case 'priority': {
          const opts = PRIORITY_LABELS.map(
            (lab, i) =>
              `<option value="${i}"${result.priority === i ? ' selected' : ''}>${i} — ${esc(lab)}</option>`,
          ).join('');
          return `<select data-scalar="priority"><option value="">—</option>${opts}</select>`;
        }
        case 'status_id': {
          // Exclut les statuts système (ex. « Fusionnée ») — non sélectionnables
          // manuellement depuis l'UI (cf. user 2026-05-21).
          const opts = statuses
            .filter((s) => !((s as { is_system?: boolean }).is_system === true))
            .map(
              (s) =>
                `<option value="${s.id}"${result.status_id === s.id ? ' selected' : ''}>${esc(s.name)}</option>`,
            )
            .join('');
          return `<select data-scalar="status_id"><option value="">—</option>${opts}</select>`;
        }
        case 'progress': {
          const v = result.progress ?? 0;
          return (
            `<span class="progress-row">` +
            `<input type="number" min="0" max="100" data-scalar="progress" value="${v}">` +
            `<span class="merge-gauge"><span class="merge-gauge-fill" style="width:${v}%"></span></span>` +
            `<span data-progress-label>${v} %</span>` +
            `</span>`
          );
        }
        case 'approver_id': {
          // Approbateur : bouton qui ouvre un picker single-select avec
          // recherche (cf. user 2026-05-21 — pas de <select> standard).
          const u = result.approver_id != null ? userMap[result.approver_id] : null;
          const label = u
            ? esc(u.is_team ? `[équipe] ${u.name}` : _userFull(u))
            : esc(t('pageActionsMerge.approver.choose'));
          const emptyCls = u ? '' : ' empty';
          return (
            `<button type="button" class="merge-approver-btn${emptyCls}" data-approver-picker="1">` +
            `<span>${label}</span><span class="caret">▾</span>` +
            `</button>`
          );
        }
      }
    }

    function _renderRow(field: ScalarField | ListField, isList: boolean): string {
      const keeper = findAction(curKeeperId);
      const absorbed = findAction(curAbsorbedId);
      if (!keeper || !absorbed) return '';
      const labelHtml = esc(t(FIELD_LABEL_KEY[field]));
      let keeperHtml: string;
      let absorbedHtml: string;
      let resultHtml: string;
      if (isList) {
        keeperHtml = _sideListHtml(field as ListField, keeper, 'keeper');
        absorbedHtml = _sideListHtml(field as ListField, absorbed, 'absorbed');
        resultHtml = _resultListHtml(field as ListField);
      } else {
        // Coche verte allumée quand la valeur de cette source correspond à
        // la valeur courante du résultat — retour visuel immédiat sur ce qui
        // a été « copié dans le résultat ».
        const sf = field as ScalarField;
        const kVal = (keeper as Record<string, unknown>)[sf];
        const aVal = (absorbed as Record<string, unknown>)[sf];
        const rVal = (result as unknown as Record<string, unknown>)[sf];
        keeperHtml = _scalarWrapperHtml(sf, 'keeper', keeper, _scalarEq(kVal, rVal));
        absorbedHtml = _scalarWrapperHtml(sf, 'absorbed', absorbed, _scalarEq(aVal, rVal));
        resultHtml = _resultScalarHtml(sf);
      }
      return (
        `<tr class="merge-row" data-field="${field}">` +
        `<td class="field">${labelHtml}</td>` +
        `<td class="keeper">${keeperHtml}</td>` +
        `<td class="absorbed">${absorbedHtml}</td>` +
        `<td class="result">${resultHtml}</td>` +
        `</tr>`
      );
    }

    function _renderHeader(): string {
      return (
        `<div class="merge-swap">` +
        `<span class="pill keeper">${esc(t('pageActionsMerge.col.keeper'))} #${curKeeperId}</span>` +
        `<button id="btn-swap" class="btn btn-secondary btn-sm" title="${esc(t('pageActionsMerge.swapTitle'))}">⇄ ${esc(t('pageActionsMerge.swap'))}</button>` +
        `<span class="pill absorbed">${esc(t('pageActionsMerge.col.absorbed'))} #${curAbsorbedId}</span>` +
        `</div>`
      );
    }

    // Lignes d'information ajoutées en queue de tableau pour rappeler les
    // actions automatiques exécutées par le backend pendant la fusion. Pas
    // de choix utilisateur : ces opérations sont systématiques.
    function _renderAutoRowsHtml(): string {
      return (
        `<tr class="merge-info"><td colspan="4" style="font-style:italic;color:var(--text2);background:#fafbfc;padding:6px 10px">` +
        esc(t('pageActionsMerge.auto.notes')) +
        `</td></tr>` +
        `<tr class="merge-info"><td colspan="4" style="font-style:italic;color:var(--text2);background:#fafbfc;padding:6px 10px">` +
        esc(t('pageActionsMerge.auto.risks')) +
        `</td></tr>`
      );
    }

    function _render(): void {
      const keeper = findAction(curKeeperId);
      const absorbed = findAction(curAbsorbedId);
      if (!keeper || !absorbed) {
        setContent(
          `<div class="error" style="padding:20px">${esc(t('pageActionsMerge.err.notFound'))}</div>`,
        );
        return;
      }
      if (keeper.merged_into_id || absorbed.merged_into_id) {
        setContent(
          `<div class="error" style="padding:20px">${esc(t('pageActionsMerge.err.alreadyMerged'))}</div>`,
        );
        return;
      }

      let rows = '';
      for (const def of ROW_ORDER) rows += _renderRow(def.field, def.list);
      rows += _renderAutoRowsHtml();

      const table =
        `<table class="merge-table">` +
        `<thead><tr>` +
        `<th style="width:160px"></th>` +
        `<th>${esc(t('pageActionsMerge.col.keeper'))} #${curKeeperId}</th>` +
        `<th>${esc(t('pageActionsMerge.col.absorbed'))} #${curAbsorbedId}</th>` +
        `<th style="width:340px">${esc(t('pageActionsMerge.col.result'))}</th>` +
        `</tr></thead><tbody>${rows}</tbody></table>`;

      const breadcrumb = `<div style="font-size:12px;margin-bottom:12px"><a href="#actions">← ${esc(t('vocabulary.action_plural'))}</a></div>`;

      setContent(
        MERGE_CSS +
          breadcrumb +
          `<div class="merge-page">` +
          `<p style="color:#445069;font-size:13px">${esc(t('pageActionsMerge.intro'))}</p>` +
          `<p class="merge-help">${esc(t('pageActionsMerge.help'))}</p>` +
          _renderHeader() +
          table +
          `<div class="merge-warn">⚠ ${esc(t('pageActionsMerge.warning'))}</div>` +
          `<div class="merge-actions">` +
          `<button id="btn-merge-cancel" class="btn btn-secondary">${esc(t('pageActionsMerge.btn.cancel'))}</button>` +
          `<button id="btn-merge-confirm" class="btn btn-danger">${esc(t('pageActionsMerge.btn.confirm'))}</button>` +
          `</div></div>`,
      );

      _wire();
    }

    // ── Wiring ─────────────────────────────────────────────────────────

    function _wire(): void {
      document.getElementById('btn-swap')?.addEventListener('click', () => {
        [curKeeperId, curAbsorbedId] = [curAbsorbedId, curKeeperId];
        // Swap (cf. user 2026-05-21) : on RÉINITIALISE le résultat avec les
        // valeurs de la nouvelle conservée. Les choix faits avant le swap
        // (overrides texte, toggles chips…) sont effacés — le swap est un
        // « repartir de zéro depuis l'autre côté ».
        const newKeeper = findAction(curKeeperId);
        if (newKeeper) result = _initResultFromKeeper(newKeeper);
        _render();
      });
      document.getElementById('btn-merge-cancel')?.addEventListener('click', () => {
        navigateTo('actions', {});
      });
      document.getElementById('btn-merge-confirm')?.addEventListener('click', () => {
        void _submit();
      });

      // Scalar : clic sur la valeur keeper/absorbed → copie dans le résultat.
      document.querySelectorAll<HTMLElement>('[data-scalar-set]').forEach((el) => {
        el.addEventListener('click', () => {
          const field = el.dataset['scalarSet'] as ScalarField;
          const source = el.dataset['source'] as 'keeper' | 'absorbed';
          const src = source === 'keeper' ? findAction(curKeeperId) : findAction(curAbsorbedId);
          if (!src) return;
          _applyScalarFromSource(field, src);
          _refreshScalarInput(field);
          _refreshScalarChecks(field);
        });
      });

      // Scalar input : édition libre du résultat.
      document
        .querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
          '[data-scalar]',
        )
        .forEach((el) => {
          el.addEventListener('input', () => {
            const field = el.dataset['scalar'] as ScalarField;
            _readScalarInput(field, el);
            _refreshScalarChecks(field);
          });
          el.addEventListener('change', () => {
            const field = el.dataset['scalar'] as ScalarField;
            _readScalarInput(field, el);
            _refreshScalarChecks(field);
          });
        });

      // List : clic sur un chip keeper/absorbed → toggle dans le résultat.
      document.querySelectorAll<HTMLElement>('[data-list-toggle]').forEach((el) => {
        el.addEventListener('click', () => {
          const field = el.dataset['listToggle'] as ListField;
          const id = parseInt(el.dataset['id'] || '0');
          if (id <= 0) return;
          _toggleInResult(field, id);
        });
      });

      // List : retrait via × côté résultat.
      document.querySelectorAll<HTMLElement>('[data-list-remove]').forEach((el) => {
        el.addEventListener('click', (e: Event) => {
          e.stopPropagation();
          const field = el.dataset['listRemove'] as ListField;
          const id = parseInt(el.dataset['id'] || '0');
          if (id <= 0) return;
          _removeFromResult(field, id);
        });
      });

      // List (projets, comités) : ajout via <select>.
      document.querySelectorAll<HTMLSelectElement>('[data-list-add]').forEach((sel) => {
        sel.addEventListener('change', () => {
          const field = sel.dataset['listAdd'] as ListField;
          const id = parseInt(sel.value || '0');
          if (id <= 0) return;
          (result[field] as number[]).push(id);
          sel.value = '';
          _refreshListRow(field);
        });
      });

      // RACI (R/C/I) : bouton "+" ouvre le picker anchored.
      document.querySelectorAll<HTMLButtonElement>('[data-raci-picker]').forEach((btn) => {
        btn.addEventListener('click', (e: Event) => {
          e.stopPropagation();
          const field = btn.dataset['raciPicker'] as 'performers' | 'consulted' | 'informed';
          _showMergeRaciPicker(field, btn);
        });
      });

      // CTAs "Tous" / "Aucun" sur les cellules liste keeper/absorbed.
      document.querySelectorAll<HTMLButtonElement>('[data-list-bulk]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const mode = btn.dataset['listBulk'] as 'all' | 'none';
          const field = btn.dataset['field'] as ListField;
          const source = btn.dataset['source'] as 'keeper' | 'absorbed';
          const src = source === 'keeper' ? findAction(curKeeperId) : findAction(curAbsorbedId);
          if (!src) return;
          _applyBulkList(field, _getList(field, src), mode);
        });
      });

      // Approbateur : ouvre le picker single-select.
      document.querySelectorAll<HTMLButtonElement>('[data-approver-picker]').forEach((btn) => {
        btn.addEventListener('click', (e: Event) => {
          e.stopPropagation();
          _showApproverPicker(btn);
        });
      });
    }

    // "Tous" : union des ids de la source dans le résultat (sans doublons).
    // "Aucun" : retire tous les ids de la source du résultat.
    function _applyBulkList(field: ListField, srcIds: number[], mode: 'all' | 'none'): void {
      const arr = result[field] as number[];
      if (mode === 'all') {
        const have = new Set<number>(arr);
        for (const id of srcIds) if (!have.has(id)) arr.push(id);
      } else {
        const remove = new Set<number>(srcIds);
        result[field] = arr.filter((id) => !remove.has(id));
      }
      _refreshListRow(field);
    }

    // Picker RACI anchored sous le bouton "+", inspiré de showRaciPicker
    // (component-action-modal/raci-picker.ts) : search + multi-select.
    // À la fermeture (clic-extérieur ou Escape), la liste sélectionnée
    // remplace result[field].
    function _showMergeRaciPicker(
      field: 'performers' | 'consulted' | 'informed',
      anchor: HTMLElement,
    ): void {
      // Ferme tout picker précédent.
      document.querySelectorAll('.merge-raci-picker-dd').forEach((el) => el.remove());

      let search = '';
      const selectedIds = new Set<number>(result[field] as number[]);

      const renderList = (): string => {
        const q = search.toLowerCase();
        const filtered = sortedUsers.filter(
          (u) =>
            !q || u.name.toLowerCase().includes(q) || (u.trigram || '').toLowerCase().includes(q),
        );
        if (filtered.length === 0) {
          return `<div style="padding:12px;color:var(--text3);font-size:12px;text-align:center">${esc(t('actionModal.picker.empty'))}</div>`;
        }
        return filtered
          .map((u) => {
            const sel = selectedIds.has(u.id);
            return (
              `<label data-uid="${u.id}" class="${sel ? 'sel' : ''}">` +
              `<input type="checkbox" value="${u.id}" ${sel ? 'checked' : ''} style="margin:0">` +
              `<span style="font-size:10px;color:var(--text3);min-width:32px;display:inline-block">${esc(u.trigram || '')}</span>` +
              `<span>${esc(u.is_team ? `[équipe] ${u.name}` : u.name)}</span>` +
              `</label>`
            );
          })
          .join('');
      };

      const dd = document.createElement('div');
      dd.className = 'merge-raci-picker-dd';
      dd.style.position = 'fixed';
      dd.innerHTML =
        `<div class="search-row">` +
        `<input type="text" class="search" placeholder="${esc(t('actionModal.picker.searchPlaceholder'))}">` +
        `</div>` +
        `<div class="pick-list">${renderList()}</div>` +
        `<div class="footer">` +
        `<button type="button" class="btn btn-primary btn-sm picker-add">${esc(t('actionModal.picker.btnAdd'))}</button>` +
        `</div>`;
      document.body.appendChild(dd);

      // Positionnement : sous le bouton, recalé si déborde à droite/bas
      // (cohérent avec component-action-modal/raci-picker.ts).
      const ddW = 260;
      const ddMaxH = 420;
      const margin = 8;
      const rect = anchor.getBoundingClientRect();
      let left = rect.right - ddW;
      if (left < margin) left = rect.left;
      if (left + ddW > window.innerWidth - margin) left = window.innerWidth - ddW - margin;
      let top = rect.bottom + 4;
      if (top + ddMaxH > window.innerHeight - margin) {
        top = Math.max(margin, rect.top - ddMaxH - 4);
      }
      dd.style.top = top + 'px';
      dd.style.left = left + 'px';

      const searchInput = dd.querySelector('input.search') as HTMLInputElement;
      const listEl = dd.querySelector('.pick-list') as HTMLElement;
      const addBtn = dd.querySelector('.picker-add') as HTMLButtonElement;
      setTimeout(() => searchInput.focus(), 0);
      searchInput.addEventListener('input', () => {
        search = searchInput.value;
        listEl.innerHTML = renderList();
      });
      listEl.addEventListener('change', (e: Event) => {
        const cb = e.target as HTMLInputElement | null;
        if (!cb || cb.tagName !== 'INPUT') return;
        const uid = parseInt(cb.value || '0');
        if (uid <= 0) return;
        if (cb.checked) selectedIds.add(uid);
        else selectedIds.delete(uid);
        const label = cb.closest('label');
        if (label) label.classList.toggle('sel', cb.checked);
      });

      const commit = (): void => {
        // Remplace result[field] par la sélection finale.
        result[field] = [...selectedIds];
        dd.remove();
        _refreshListRow(field);
        document.removeEventListener('mousedown', onDocClick, true);
        document.removeEventListener('keydown', onEsc, true);
      };
      const onDocClick = (e: MouseEvent): void => {
        const target = e.target as Node | null;
        if (!target) return;
        if (dd.contains(target) || anchor.contains(target)) return;
        commit();
      };
      const onEsc = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          commit();
        }
      };
      addBtn.addEventListener('click', (e: Event) => {
        e.stopPropagation();
        commit();
      });
      // setTimeout pour ne pas attraper l'événement d'ouverture lui-même.
      setTimeout(() => {
        document.addEventListener('mousedown', onDocClick, true);
        document.addEventListener('keydown', onEsc, true);
      }, 0);
    }

    // Picker single-select pour l'approbateur (résultat). Recherche + clic-
    // direct sur un user → commit immédiat + ferme. Bouton "Effacer" pour
    // remettre à null.
    function _showApproverPicker(anchor: HTMLElement): void {
      document.querySelectorAll('.merge-raci-picker-dd').forEach((el) => el.remove());

      let search = '';

      const renderList = (): string => {
        const q = search.toLowerCase();
        const filtered = sortedUsers.filter(
          (u) =>
            !q || u.name.toLowerCase().includes(q) || (u.trigram || '').toLowerCase().includes(q),
        );
        if (filtered.length === 0) {
          return `<div style="padding:12px;color:var(--text3);font-size:12px;text-align:center">${esc(t('actionModal.picker.empty'))}</div>`;
        }
        return filtered
          .map((u) => {
            const sel = result.approver_id === u.id;
            return (
              `<label data-uid-pick="${u.id}" class="${sel ? 'sel' : ''}" style="${sel ? 'font-weight:600' : ''}">` +
              `<span style="font-size:10px;color:var(--text3);min-width:32px;display:inline-block">${esc(u.trigram || '')}</span>` +
              `<span>${esc(u.is_team ? `[équipe] ${u.name}` : u.name)}</span>` +
              `</label>`
            );
          })
          .join('');
      };

      const dd = document.createElement('div');
      dd.className = 'merge-raci-picker-dd';
      dd.style.position = 'fixed';
      dd.innerHTML =
        `<div class="search-row">` +
        `<input type="text" class="search" placeholder="${esc(t('actionModal.picker.searchPlaceholder'))}">` +
        `</div>` +
        `<div class="pick-list">${renderList()}</div>` +
        `<div class="footer">` +
        `<button type="button" class="btn btn-secondary btn-sm picker-clear">${esc(t('pageActionsMerge.approver.clear'))}</button>` +
        `</div>`;
      document.body.appendChild(dd);

      const ddW = 280;
      const ddMaxH = 420;
      const margin = 8;
      const rect = anchor.getBoundingClientRect();
      let left = rect.left;
      if (left + ddW > window.innerWidth - margin) left = window.innerWidth - ddW - margin;
      let top = rect.bottom + 4;
      if (top + ddMaxH > window.innerHeight - margin) {
        top = Math.max(margin, rect.top - ddMaxH - 4);
      }
      dd.style.top = top + 'px';
      dd.style.left = left + 'px';
      dd.style.minWidth = ddW + 'px';

      const searchInput = dd.querySelector('input.search') as HTMLInputElement;
      const listEl = dd.querySelector('.pick-list') as HTMLElement;
      const clearBtn = dd.querySelector('.picker-clear') as HTMLButtonElement;
      setTimeout(() => searchInput.focus(), 0);
      searchInput.addEventListener('input', () => {
        search = searchInput.value;
        listEl.innerHTML = renderList();
      });

      // Clic direct sur un label → commit immédiat (single-select).
      listEl.addEventListener('click', (e: Event) => {
        const label = (e.target as HTMLElement | null)?.closest<HTMLElement>(
          'label[data-uid-pick]',
        );
        if (!label) return;
        const uid = parseInt(label.dataset['uidPick'] || '0');
        if (uid <= 0) return;
        result.approver_id = uid;
        close();
        _refreshScalarInput('approver_id');
        _refreshScalarChecks('approver_id');
      });

      clearBtn.addEventListener('click', (e: Event) => {
        e.stopPropagation();
        result.approver_id = null;
        close();
        _refreshScalarInput('approver_id');
        _refreshScalarChecks('approver_id');
      });

      const close = (): void => {
        dd.remove();
        document.removeEventListener('mousedown', onDocClick, true);
        document.removeEventListener('keydown', onEsc, true);
      };
      const onDocClick = (e: MouseEvent): void => {
        const target = e.target as Node | null;
        if (!target) return;
        if (dd.contains(target) || anchor.contains(target)) return;
        close();
      };
      const onEsc = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          close();
        }
      };
      setTimeout(() => {
        document.addEventListener('mousedown', onDocClick, true);
        document.addEventListener('keydown', onEsc, true);
      }, 0);
    }

    function _applyScalarFromSource(field: ScalarField, src: MergeAction): void {
      switch (field) {
        case 'text':
          result.text = src.text ?? null;
          break;
        case 'deadline':
          result.deadline = src.deadline ?? null;
          break;
        case 'deadline_revised':
          result.deadline_revised = src.deadline_revised ?? null;
          break;
        case 'priority':
          result.priority = src.priority ?? null;
          break;
        case 'status_id':
          result.status_id = src.status_id ?? null;
          break;
        case 'progress':
          result.progress = src.progress ?? 0;
          break;
        case 'approver_id':
          result.approver_id = src.approver_id ?? null;
          break;
      }
    }

    function _readScalarInput(
      field: ScalarField,
      el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
    ): void {
      const v = el.value;
      switch (field) {
        case 'text':
          result.text = v || null;
          break;
        case 'deadline':
          result.deadline = v || null;
          break;
        case 'deadline_revised':
          result.deadline_revised = v || null;
          break;
        case 'priority':
          result.priority = v === '' ? null : parseInt(v);
          break;
        case 'status_id':
          result.status_id = v === '' ? null : parseInt(v);
          break;
        case 'progress': {
          result.progress = Math.max(0, Math.min(100, parseInt(v) || 0));
          // Sync visuel : la jauge et le label %age suivent l'input en live.
          const row = document.querySelector('tr.merge-row[data-field="progress"] td.result');
          const fill = row?.querySelector('.merge-gauge-fill') as HTMLElement | null;
          const label = row?.querySelector('[data-progress-label]') as HTMLElement | null;
          if (fill) fill.style.width = result.progress + '%';
          if (label) label.textContent = result.progress + ' %';
          break;
        }
        case 'approver_id':
          result.approver_id = v === '' ? null : parseInt(v);
          break;
      }
    }

    // Met à jour l'input du résultat sans rerendre toute la table (les autres
    // inputs garderaient leur focus). Tactique : on remplace juste la cellule
    // résultat du champ visé.
    function _refreshScalarInput(field: ScalarField): void {
      const row = document.querySelector(`tr.merge-row[data-field="${field}"]`);
      const cell = row?.querySelector('td.result');
      if (!cell) return;
      cell.innerHTML = _resultScalarHtml(field);
      const input = cell.querySelector('[data-scalar]') as
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
      if (input) {
        input.addEventListener('input', () => {
          _readScalarInput(field, input);
          _refreshScalarChecks(field);
        });
        input.addEventListener('change', () => {
          _readScalarInput(field, input);
          _refreshScalarChecks(field);
        });
      }
      // Approbateur : rewire le bouton vers le picker single-select.
      const approverBtn = cell.querySelector('[data-approver-picker]') as HTMLButtonElement | null;
      if (approverBtn) {
        approverBtn.addEventListener('click', (e: Event) => {
          e.stopPropagation();
          _showApproverPicker(approverBtn);
        });
      }
    }

    // Sync la coche verte côté keeper/absorbed pour un champ scalaire. À
    // appeler après tout changement de result[field] : la coche s'allume sur
    // la source dont la valeur correspond, s'éteint sinon. Pour Progress on
    // doit aussi rerendre la jauge inline si la valeur de la source a
    // changé visuellement — pas le cas ici (les sources ne bougent pas),
    // on touche juste la classe .checked.
    function _refreshScalarChecks(field: ScalarField): void {
      const keeper = findAction(curKeeperId);
      const absorbed = findAction(curAbsorbedId);
      if (!keeper || !absorbed) return;
      const row = document.querySelector(`tr.merge-row[data-field="${field}"]`);
      if (!row) return;
      const rVal = (result as unknown as Record<string, unknown>)[field];
      const kVal = (keeper as Record<string, unknown>)[field];
      const aVal = (absorbed as Record<string, unknown>)[field];
      const kEl = row.querySelector('td.keeper .merge-pill[data-scalar-set]');
      const aEl = row.querySelector('td.absorbed .merge-pill[data-scalar-set]');
      if (kEl) kEl.classList.toggle('checked', _scalarEq(kVal, rVal));
      if (aEl) aEl.classList.toggle('checked', _scalarEq(aVal, rVal));
    }

    function _toggleInResult(field: ListField, id: number): void {
      const arr = result[field] as number[];
      const idx = arr.indexOf(id);
      if (idx === -1) arr.push(id);
      else arr.splice(idx, 1);
      _refreshListRow(field);
    }

    function _removeFromResult(field: ListField, id: number): void {
      const arr = result[field] as number[];
      const idx = arr.indexOf(id);
      if (idx !== -1) arr.splice(idx, 1);
      _refreshListRow(field);
    }

    // Rerend les 3 cellules d'une ligne liste (keeper visual state, absorbed
    // visual state, result content). Rewire le toggle et le picker locaux.
    function _refreshListRow(field: ListField): void {
      const keeper = findAction(curKeeperId);
      const absorbed = findAction(curAbsorbedId);
      if (!keeper || !absorbed) return;
      const row = document.querySelector(`tr.merge-row[data-field="${field}"]`);
      if (!row) return;
      const keeperCell = row.querySelector('td.keeper') as HTMLElement | null;
      const absorbedCell = row.querySelector('td.absorbed') as HTMLElement | null;
      const resultCell = row.querySelector('td.result') as HTMLElement | null;
      if (keeperCell) keeperCell.innerHTML = _sideListHtml(field, keeper, 'keeper');
      if (absorbedCell) absorbedCell.innerHTML = _sideListHtml(field, absorbed, 'absorbed');
      if (resultCell) resultCell.innerHTML = _resultListHtml(field);
      // Rewire les nouveaux nœuds.
      row.querySelectorAll<HTMLElement>('[data-list-toggle]').forEach((el) => {
        el.addEventListener('click', () => {
          const id = parseInt(el.dataset['id'] || '0');
          if (id > 0) _toggleInResult(field, id);
        });
      });
      row.querySelectorAll<HTMLElement>('[data-list-remove]').forEach((el) => {
        el.addEventListener('click', (e: Event) => {
          e.stopPropagation();
          const id = parseInt(el.dataset['id'] || '0');
          if (id > 0) _removeFromResult(field, id);
        });
      });
      row.querySelectorAll<HTMLSelectElement>('[data-list-add]').forEach((sel) => {
        sel.addEventListener('change', () => {
          const id = parseInt(sel.value || '0');
          if (id <= 0) return;
          (result[field] as number[]).push(id);
          sel.value = '';
          _refreshListRow(field);
        });
      });
      row.querySelectorAll<HTMLButtonElement>('[data-raci-picker]').forEach((btn) => {
        btn.addEventListener('click', (e: Event) => {
          e.stopPropagation();
          const f = btn.dataset['raciPicker'] as 'performers' | 'consulted' | 'informed';
          _showMergeRaciPicker(f, btn);
        });
      });
      row.querySelectorAll<HTMLButtonElement>('[data-list-bulk]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const mode = btn.dataset['listBulk'] as 'all' | 'none';
          const source = btn.dataset['source'] as 'keeper' | 'absorbed';
          const src = source === 'keeper' ? keeper : absorbed;
          _applyBulkList(field, _getList(field, src), mode);
        });
      });
    }

    async function _submit(): Promise<void> {
      // Confirmation explicite avant une opération destructive et irréversible
      // (l'action absorbée disparaît, ses références migrent). Le bandeau
      // d'avertissement passif ne suffit pas (audit UX 2026-06).
      const confirmed = await confirmStackModal({
        message: t('pageActionsMerge.confirm.message', {
          keeper: curKeeperId,
          absorbed: curAbsorbedId,
        }),
        danger: true,
        confirmLabel: t('pageActionsMerge.btn.confirm'),
      });
      if (!confirmed) return;

      const btn = document.getElementById('btn-merge-confirm') as HTMLButtonElement | null;
      if (btn) btn.disabled = true;

      // L'overrides est l'état complet du résultat : on laisse le backend
      // appliquer (no-op pour les champs identiques au keeper). Simplifie le
      // contrat et permet à l'utilisateur d'avoir saisi une valeur ni dans A
      // ni dans B (cf. ajout de nouveaux Informés via le picker).
      const overrides: Record<string, unknown> = {
        text: result.text,
        deadline: result.deadline,
        deadline_revised: result.deadline_revised,
        priority: result.priority,
        status_id: result.status_id,
        progress: result.progress,
        approver_id: result.approver_id,
        performers: result.performers,
        consulted: result.consulted,
        informed: result.informed,
        project_ids: result.project_ids,
        committee_ids: result.committee_ids,
      };

      try {
        await api.mergeActions({
          keeper_id: curKeeperId,
          absorbed_id: curAbsorbedId,
          overrides,
        });
        toast(
          t('pageActionsMerge.toast.success', { keeper: curKeeperId, absorbed: curAbsorbedId }),
        );
        // Drapeau d'animation : la liste actions lit ce flag à son
        // prochain rendu pour faire disparaître la ligne absorbée et
        // mettre en évidence la conservée. TTL court (5 s) pour éviter
        // qu'une ouverture tardive de la page n'anime à contre-temps.
        try {
          sessionStorage.setItem(
            'merge:lastResult',
            JSON.stringify({ keeper: curKeeperId, absorbed: curAbsorbedId, ts: Date.now() }),
          );
        } catch {
          /* sessionStorage indisponible (mode privé Safari, etc.) — animation perdue, OK */
        }
        navigateTo('actions', {});
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : t('common.error');
        toast(t('pageActionsMerge.toast.error', { msg }), 'error');
        if (btn) btn.disabled = false;
      }
    }

    _render();
  },
);

export {};
